import { useLocation, useNavigate, useParams } from 'react-router-dom'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  PartnerType,
  providerToAuthCloud,
  useAccountId,
  useChatContext,
  useUserType,
  type AccountDataSelection,
} from '@labrav/common'
import { useStyles } from './ResaleAuthorizationWrapperUIV2.styles'
import { useDispatch, useSelector } from '../../store'
import type { RootState } from '../../store'
import { get, isEmpty } from 'lodash'
import { getProducts } from '../../modules/productsListing/action'
import {
  ButtonV2,
  CheckBoxFieldv2,
  ColumnActionWithMenuV2,
  ColumnMenuOption,
  DateTime,
  Filters,
  FiltersApplied,
  LabraToolTipV2,
  RadioButtonsV2,
  SectionProps,
  StatusChipV4,
  useAuth,
} from '@labrav/react-components'
import { useSyncCloudMarketplace } from '@labrav/common'
import { useSyncFlyoutRouteParams } from '../../common/utils/hooks/useSyncFlyoutRouteParams'
import { setSelectedAccount } from '@labrav/common'
import { CloudType } from '@labrav/common'
import { FlyoutFunction } from '../../modules/productsListing/types'
import {
  clearResaleAuthorizations,
  getResellerAgreementData,
  updateAgreementId,
  updateAgreementStatus,
  updateResellerData,
  deleteCurrentResaleAuthorization,
} from '../../modules/resaleAuthorization/utils'
import { setResaleAuthorizationsPageNumber } from '../../modules/productListingFilter/utils'
import { useFlag, useFlagValue } from '@labrav/flags'
import { ProductSelectorDropdown } from '../../common/components/ProductSelectorDropdown/ProductSelectorDropdown'
import { ResellerAgreementStatus } from '../../types/flyout'
import type { ResaleAuthorizationsResellerAgreements } from '../../types/resaleAuthorization'
import type { DetailedOffer } from '../PrivateOfferForm/DetailedOffer/DetailedOffer'
import { useTimeZone } from '../../common/utils/hooks/useTimeZone'
import { getStatusAndLabelForResale } from '../../common/utils/getStatusAndLabelForResale'
import { ShortenedNameV2 } from '@labrav/common'
import { DateTime as DateTimeLuxon } from 'luxon'
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import {
  searchDropDownOptionMapping,
  searchDropDownOptions,
  standardLabraDateFormat,
} from '@labrav/common'
import { ShowExpirationDateWithIcon } from '../../common/components/ShowExpirationDateWithIcon/ShowExpirationDateWithIcon'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { ListingCreationPage } from '../../common/components/ListingCreationPage/ListingCreationPage'
import { expiryDateMapping } from '../../utils/privateOffersFilterMapping'
import {
  type CustomCloudOptions,
  CloudAccountChangeV2,
  type CloudAccountChangeParams,
} from '@labrav/common'
import { getPricesArray } from '../../modules/resaleAuthorization/utils'
import { PriceTypes } from '../../modules/privateOffer/types'
import { updateAppAlert } from '@labrav/common'
import {
  useCloudAccounts,
  getSelectedAccountIdWithCloud,
  extractAccountIdFromCombinedId,
} from '@labrav/common'
import { actionTypeWrapper } from '@labrav/common'
import { setSelectedProductOnListing } from '../../modules/productsListing/action'
import { clearProductsListing } from '../../modules/productsListing/action'
import { useScreenSize } from '@labrav/common'
import { SearchBoxComponent } from '@labrav/react-components'
import clsx from 'clsx'
import { SearchResultsCount } from '@labrav/common'
import { CloudMarketplace } from '../../types/index'
import { useCommonStyles } from '../../common/styles/common.styles'
import { ResponsiveColumn } from '../../common/components/ResponsiveColumn/ResponsiveColumn'
import { getLabelFromMapping } from '@labrav/common'
import {
  setCloudMarketplaceProductListing,
  setCreatePrivateOfferModalOpen,
} from '../../modules/productListingFilter/action'
import {
  startLoading,
  stopLoading,
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
} from '@labrav/common'
import {
  resaleAuthorizationStatusMicrosoft,
  statusMappingForLabraV2UI,
} from './constants'
import { DeleteAuthorizationModal } from './DeleteAuthorizationModal'
import {
  getResaleMenuOptions,
  getResaleOfferForDetailedView,
  getResellersDataBasedOnCloud,
} from './helpers'
import { ModalToCreateResaleAuthorization } from '../../common/components/ModalToCreateResaleAuthorization/ModalToCreateResaleAuthorization'
import { ResaleAuthorizationsListV2 } from '../ResaleAuthorizationListV2/ResaleAuthorizationListV2'
import type { ResaleAuthorizationsListV2Props } from '../ResaleAuthorizationListV2/types'
import type {
  ResaleAuthorizationsListState,
  ResaleAuthorizationsV2Props,
} from './types'

export { getResaleMenuOptions } from './helpers'
export type {
  ResaleAuthorizationsV2Props,
  ResaleAuthorizationsV2BaseProps,
} from './types'

const getResaleAuthorizationSearchInput = (selectedOption: string) =>
  (
    searchDropDownOptionMapping.resaleAuthorizationOptions as Record<
      string,
      string
    >
  )[selectedOption]

const ResaleAuthorizationsWrapperUIV2: React.FC<
  ResaleAuthorizationsV2Props
> = ({ externalBaseURL, search, crmOpportunityId, tableEl }) => {
  const { classes } = useStyles({ isExternal: !!externalBaseURL })
  const { classes: commonClasses } = useCommonStyles()
  const dispatch = useDispatch()
  const [selectedProductId, setSelectedProductId] = useState<string>('')

  const [copiedResellerAuthorizationLink, setCopiedResellerAuthorizationLink] =
    useState(false)
  const { pathname, search: locationSearch } = useLocation()
  const { pageSize: pageSizeValue, screenWidth } = useScreenSize()

  const productsLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS)
  )

  const { value: isAzureResaleAuthorizationMappingManagerEnabled } =
    useFlagValue('azureMarketplaceResaleAuthorizationMappingManager')

  const isAdmin = useUserType() === PartnerType.Admin
  const { canDo, loading: authLoading } = useAuth()
  //TO DO: https://ibexlabs.atlassian.net/browse/LT-3594 Remove this useEffect and its processing
  const {
    partner_id: accountIdFromParams,
    cloud: routeCloud,
    accountId: accountIdFromRoute,
    secureKey: secureKeyFromURL,
    partnerId: partnerIdFromURL,
  } = useParams<{
    partner_id?: string
    cloud?: string
    accountId?: string
    secureKey?: string
    partnerId?: string
  }>()
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )

  const [openNewResaleAuthorizationModal, setOpenNewResaleAuthorizationModal] =
    useState<boolean>(false)

  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [selectedAgreementForDelete, setSelectedAgreementForDelete] =
    useState<ResaleAuthorizationsResellerAgreements | null>(null)

  const handleOpenDeleteModal = (
    agreement: ResaleAuthorizationsResellerAgreements
  ) => {
    setSelectedAgreementForDelete(agreement)
    setOpenDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setSelectedAgreementForDelete(null)
  }

  const deleteLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.DELETE_RESALE_AUTHORIZATION)
  )
  const handleConfirmDeleteModal = async () => {
    if (!selectedAgreementForDelete) return

    await dispatch(
      deleteCurrentResaleAuthorization(
        selectedAgreementForDelete.id as string,
        undefined,
        accountId,
        cloudMarketplace
      )
    )
    handleCloseDeleteModal()
  }

  const [searchNow, setSearchNow] = useState(false)
  const [showCrossMark, setShowCrossMark] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [selectedOption, setSelectedOption] = useState(
    searchDropDownOptions.resaleAuthorizationOptions[0]
  )
  const [expiryDate, setExpiryDate] = useState<string>('')
  const [selectedOfferStatusCheckboxes, setSelectedOfferStatusCheckboxes] =
    useState<string[]>([])
  const [appliedFilteredValues, setAppliedFilteredValues] = useState<string[]>(
    []
  )

  const accountIdFromHook = useAccountId({ product: 'flyout' }) || ''
  const accountId =
    accountIdFromRoute || accountIdFromParams || accountIdFromHook || ''

  const navigate = useNavigate()

  useSyncCloudMarketplace()

  // Sync route params to selectedAccountIds for FLYOUT - single source of truth
  useSyncFlyoutRouteParams(!!externalBaseURL)

  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const cloudMarketplace: CloudMarketplace = routeCloud
    ? (routeCloud.toUpperCase() as CloudMarketplace)
    : selectedCloudTypeProductListing

  // Get marketplace cloud accounts with unique IDs for CloudAccountChangeV2
  const marketplaceCloudAccounts = useCloudAccounts('flyout')

  const filteredCloudAccounts = useMemo(() => {
    if (authLoading) {
      return marketplaceCloudAccounts.map(account => ({
        ...account,
        disabled:
          account.provider === 'GCP' ||
          (account.provider === 'AZURE' &&
            !isAzureResaleAuthorizationMappingManagerEnabled),
      }))
    }
    // Resale list: same as private offers — gate account switching on marketplace *read*.
    return marketplaceCloudAccounts.map(account => {
      const hasPermission = canDo({
        cloud: providerToAuthCloud(account.provider),
        service: 'marketplace',
        action: 'view',
      })
      const accountProvider = account.provider.toUpperCase()
      const isAzure = accountProvider === 'AZURE'
      const isGcp = accountProvider === 'GCP'

      return {
        ...account,
        disabled:
          isGcp ||
          (isAzure
            ? !isAzureResaleAuthorizationMappingManagerEnabled
            : !hasPermission),
      }
    })
  }, [
    authLoading,
    canDo,
    isAzureResaleAuthorizationMappingManagerEnabled,
    marketplaceCloudAccounts,
  ])

  // Get the selected account ID with cloud for matching in dropdown
  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    accountId,
    'flyout',
    routeCloud || selectedCloudTypeProductListing
  )

  const allAccounts = useSelector(
    (state: RootState) => state.accountSelection?.availableAccounts || []
  ) as AccountDataSelection[]

  useEffect(() => {
    if (
      routeCloud &&
      routeCloud.toUpperCase() === 'GCP' &&
      accountIdFromRoute
    ) {
      const flyoutAccounts = allAccounts.filter(
        acc => acc.product?.toLowerCase() === 'flyout'
      )

      // Check for AWS account first (prefer AWS)
      let awsAccountId = flyoutAccounts.find(
        acc => acc.id === accountIdFromRoute && acc.cloud === 'AWS'
      )?.id

      if (!awsAccountId) {
        awsAccountId = flyoutAccounts.find(acc => acc.cloud === 'AWS')?.id
      }

      // If AWS found, redirect to AWS
      if (awsAccountId) {
        navigate(`/flyout/aws/${awsAccountId}/reseller-authorizations/sent`, {
          replace: true,
        })
        return
      }

      // If no AWS, check for Azure
      let azureAccountId = flyoutAccounts.find(
        acc => acc.id === accountIdFromRoute && acc.cloud === 'AZURE'
      )?.id

      if (!azureAccountId) {
        azureAccountId = flyoutAccounts.find(acc => acc.cloud === 'AZURE')?.id
      }

      // If Azure found, redirect to Azure
      if (azureAccountId) {
        navigate(
          `/flyout/azure/${azureAccountId}/reseller-authorizations/sent`,
          {
            replace: true,
          }
        )
      }
    }
  }, [routeCloud, accountIdFromRoute, navigate, allAccounts])

  const resaleAuthorizationStatus = {
    ...(cloudMarketplace === 'AZURE' ? resaleAuthorizationStatusMicrosoft : {}),
  }

  const handleOpenResaleAuthorizationModal = useCallback(() => {
    setOpenNewResaleAuthorizationModal(true)
  }, [openNewResaleAuthorizationModal])
  const handleCloseResaleAuthorizationModal = useCallback(() => {
    setOpenNewResaleAuthorizationModal(false)
  }, [openNewResaleAuthorizationModal])

  useEffect(() => {
    if (externalBaseURL) {
      dispatch(setCreatePrivateOfferModalOpen(openNewResaleAuthorizationModal))
      return () => {
        dispatch(setCreatePrivateOfferModalOpen(false))
      }
    }
  }, [openNewResaleAuthorizationModal, dispatch])

  const columnMaxWidth = useMemo(
    () => `${(screenWidth * (cloudMarketplace === 'AWS' ? 20 : 11)) / 100}px`,
    [screenWidth, cloudMarketplace]
  )

  const { products, selectedProduct: productId } =
    useFlyOutProductsBasedOnSelectedCloud()

  const selectedProductDetails = useMemo(() => {
    return (
      products.find(product => product.productId === productId) || ({} as any)
    )
  }, [products, productId])

  const [selectedResale, setSelectedResale] = useState<DetailedOffer>(
    {} as DetailedOffer
  )

  const { resellerAgreements, count, pageNumber } =
    useSelector<ResaleAuthorizationsListState>(
      state => state.cppoReseller as ResaleAuthorizationsListState
    )
  useChatContext('resellerAgreements', () => ({
    cloud: cloudMarketplace,
    productId,
    search: inputValue,
    searchField: selectedOption,
    status: selectedOfferStatusCheckboxes,
    direction: 'sent',
    visible: { count, page: pageNumber },
  }))

  const lastFetchKeyRef = useRef<string>('')

  const handleProductChange = useCallback(
    (productId: string) => {
      setSelectedProductId(productId)
    },
    [productId]
  )

  const getProductsAndClearAuthorization = async () => {
    await dispatch(clearResaleAuthorizations())
    await dispatch(getProducts(accountId, selectedCloudTypeProductListing))
  }

  useEffect(() => {
    if (!isEmpty(accountId) && !externalBaseURL) {
      if (isEmpty(products)) {
        getProductsAndClearAuthorization()
      }
    }
  }, [accountId, selectedCloudTypeProductListing])

  useEffect(() => {
    if (
      !externalBaseURL &&
      productId !== undefined &&
      !isEmpty(products) &&
      !productsLoading
    ) {
      dispatch(
        getResellerAgreementData({
          partnerId: accountId,
          pageSize: pageSizeValue,
          pageNumber: 1,
          productId,
          cloud: cloudMarketplace,
          crmOpportunityId,
          ...(!isEmpty(inputValue) && !isEmpty(selectedOption)
            ? {
                input: inputValue,
                selectedInput:
                  getResaleAuthorizationSearchInput(selectedOption),
                sectionName: 'resale-authorizations',
              }
            : {}),
          expiryDays: expiryDate,
          offerStatus: selectedOfferStatusCheckboxes,
        })
      )
    }
    return () => {
      dispatch(
        updateAppAlert({
          message: undefined,
          messageType: 'INFO',
          autoClose: true,
        })
      )
      dispatch(clearResaleAuthorizations())
      dispatch(setResaleAuthorizationsPageNumber(0))
    }
  }, [accountId, productId, searchNow, cloudMarketplace, productsLoading])

  const { timeZone } = useTimeZone()
  const [openModal, setOpenModal] = useState<boolean>(false)
  const viewCPPODetailed = useCallback(
    (agreement: ResaleAuthorizationsResellerAgreements) => () => {
      const offer = getResaleOfferForDetailedView(agreement, timeZone)
      setSelectedResale(offer)
      setOpenModal(true)
    },
    []
  )
  const handleClose = useCallback(() => {
    setOpenModal(false)
  }, [openModal])

  const updateCPPOData = useCallback(
    (agreementId: string) => async () => {
      await dispatch(updateResellerData(accountId, agreementId))
    },
    []
  )
  const updateStatus = useCallback(
    (agreementId: string) => (event: SelectChangeEvent) => {
      dispatch(
        updateAgreementStatus(
          agreementId,
          (event?.target?.value as ResellerAgreementStatus) ||
            ResellerAgreementStatus.IN_PROGRESS
        )
      )
    },
    [resellerAgreements]
  )
  const updateId = useCallback(
    (agreementId: string) =>
      async (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      ) => {
        dispatch(updateAgreementId(agreementId, event?.target?.value || ''))
      },
    [resellerAgreements]
  )
  const getCustomObjectForAdmin = () =>
    resellerAgreements.map(agreement => {
      return {
        resellerName: get(
          agreement,
          'resellersInfo[0].resellerCompanyName',
          '-'
        ),
        id: agreement.id,
        productName: (
          <ShortenedNameV2
            name={get(agreement, 'products[0].productName', '-')}
          />
        ) as never,
        createdAt: (<DateTime date={agreement.createdAt as string} />) as never,
        awsAgreementId: (
          <TextField
            onChange={updateId(agreement.id as string)}
            key={'awsAgreementId'}
            id={`awsAgreementId-${agreement.id}`}
            label={'Enter AWS agreement ID'}
            placeholder="N/A"
            variant="standard"
            defaultValue={agreement.awsAgreementId}
            className={`${classes.resizeFontTextField} ${classes.defaultValueWidth}`}
            slotProps={{
              htmlInput: {
                'data-testid': `agreement-id-${agreement.id}`,
                'data-id': `agreement-id-${agreement.id}`,
              },
            }}
          />
        ) as never,
        status: (
          <FormControl>
            <InputLabel classes={{ shrink: commonClasses.shrink }}>
              Agreement status
            </InputLabel>
            <Select
              labelId="Agreement Status"
              label="Agreement status"
              defaultValue={
                agreement?.status ?? ResellerAgreementStatus.IN_PROGRESS
              }
              onChange={updateStatus(agreement.id as string)}
              inputProps={{ 'data-testid': 'input' }}
              variant="standard"
              className={classes.selectField}
              data-testid={`status-${agreement.id}`}
            >
              {Object.keys(statusMappingForLabraV2UI).map(status => (
                <MenuItem
                  data-testid="menu-item"
                  value={statusMappingForLabraV2UI[status]}
                  className={classes.menuRoot}
                  key={status}
                >
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) as never,
        actions: (
          <div className={classes.buttonContainer}>
            <ButtonV2
              className={classes.submitButton}
              onClick={updateCPPOData(agreement.id as string)}
              data-testid={`send-details-${agreement.id}`}
            >
              Submit
            </ButtonV2>
            <ButtonV2
              className={classes.submitButton}
              onClick={viewCPPODetailed(agreement)}
              data-testid={`view-details-${agreement.id}`}
            >
              Details
            </ButtonV2>
          </div>
        ) as never,
      }
    })
  const baseUrl = useMemo(() => {
    if (externalBaseURL) return externalBaseURL
    if (routeCloud && accountId) {
      return `/flyout/${routeCloud}/${accountId}/products-listing`
    }
    return '/flyout/products-listing'
  }, [externalBaseURL, routeCloud, accountId])

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      // Extract original accountId from the combined format "accountId-product-cloud"
      const extractedAccountId = extractAccountIdFromCombinedId(
        params.accountId
      )
      const { cloud } = params

      const cloudPath = cloud.toLowerCase()

      await dispatch(startLoading(LoadingTypes.GENERAL))

      await dispatch(
        setSelectedAccount(extractedAccountId, 'flyout', cloud as CloudType)
      )

      await dispatch(
        actionTypeWrapper(
          cloudMarketplace,
          setSelectedProductOnListing(undefined)
        )
      )

      await dispatch(actionTypeWrapper(cloud, clearProductsListing()))
      await dispatch(setCloudMarketplaceProductListing(cloud))

      await dispatch(clearResaleAuthorizations())
      await dispatch(getProducts(extractedAccountId, cloud))

      await dispatch(stopLoading(LoadingTypes.GENERAL))

      const pathname =
        externalBaseURL && routeCloud && accountId
          ? `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${cloudPath}/accounts/${extractedAccountId}/product-listing/offers-authorizations/resale-authorizations`
          : `/flyout/${cloudPath}/${extractedAccountId}/reseller-authorizations/sent`
      navigate({ pathname, search: locationSearch }, { replace: true })

      setInputValue('')
      setShowCrossMark(false)
      setSearchNow(false)
      setExpiryDate('')
      setSelectedOfferStatusCheckboxes([])
      setAppliedFilteredValues([])
    },
    [dispatch, navigate, cloudMarketplace]
  )

  const handleSelectOption =
    (agreement: ResaleAuthorizationsResellerAgreements) =>
    async (option: ColumnMenuOption) => {
      if (option.key === 'delete') {
        handleOpenDeleteModal(agreement)
        return
      }
      if (option.route) {
        navigate({ pathname: option.route, search })
      }
      if (option?.key === 'copy-reseller-authorization-url') {
        if (!agreement?.acceptanceLink) {
          console.error('No offer link found')
          return
        }
        navigator.clipboard
          .writeText(agreement?.acceptanceLink || '')
          .then(() => {
            setCopiedResellerAuthorizationLink(true)
            setTimeout(() => {
              setCopiedResellerAuthorizationLink(false)
            }, 2000)
          })
          .catch(error => {
            console.error('Error copying URL to clipboard:', error)
          })
      }
    }
  const handleSearchClick = async () => {
    if (inputValue.trim() !== '') {
      setSearchNow(true)
      setShowCrossMark(true)
      await dispatch(clearResaleAuthorizations())
      dispatch(
        getResellerAgreementData({
          partnerId: accountId,
          pageSize: pageSizeValue,
          pageNumber: 1,
          productId,
          cloud: cloudMarketplace,
          crmOpportunityId,
          input: inputValue,
          selectedInput: getResaleAuthorizationSearchInput(selectedOption),
          sectionName: 'resale-authorizations',
        })
      )
    }
  }

  const getCustomObjectForLabraUI = () =>
    resellerAgreements.map(agreement => {
      const productId =
        cloudMarketplace === 'AWS'
          ? get(agreement, 'products[0].awsProductId', '') ||
            agreement.awsProductId
          : agreement.labraProductId
      const productDetail = products.find(
        productItem => productItem.productId === productId
      )

      const { resellerName, status, authorizationName, expiredAt } =
        getResellersDataBasedOnCloud({
          cloud: cloudMarketplace,
          resellerAgreement: agreement,
        })

      const statusDetails = getStatusAndLabelForResale(
        (status as ResellerAgreementStatus) ||
          ResellerAgreementStatus.IN_PROGRESS,
        agreement.draft
      )
      return {
        id: agreement?.id,
        companyName: (
          <ResponsiveColumn
            linkText={agreement?.companyName || '-'}
            maxWidth={columnMaxWidth}
          />
        ),
        resellerName: (
          <ResponsiveColumn linkText={resellerName} maxWidth={columnMaxWidth} />
        ),
        labraAuthorizationId: (
          <ResponsiveColumn
            linkText={agreement.id as string}
            maxWidth={columnMaxWidth}
          />
        ) as never,
        awsAuthorizationId: (
          <ResponsiveColumn
            linkText={agreement.awsAgreementId || '-'}
            maxWidth={columnMaxWidth}
          />
        ) as never,
        productName: (
          <ResponsiveColumn
            linkText={
              get(agreement, 'products[0].productName', '') ||
              productDetail?.productName ||
              '-'
            }
            linkUrl={productDetail?.marketplaceUrl || ''}
            maxWidth={columnMaxWidth}
          />
        ) as never,
        createdAt: (!isEmpty(agreement.createdAt)
          ? DateTimeLuxon.fromISO(agreement.createdAt as string).toFormat(
              standardLabraDateFormat
            )
          : '-') as never,
        expiredAt: !isEmpty(expiredAt) ? (
          <ShowExpirationDateWithIcon
            date={expiredAt || ''}
            daysToCompare={5}
            daysLessLimit={15}
            expirePrefix="Ends in"
            shouldShowExpiringSoon={
              cloudMarketplace === 'AZURE'
                ? !!status &&
                  [
                    ResellerAgreementStatus.OFFER_SENT_RESELLER,
                    ResellerAgreementStatus.OFFER_VIEWED_RESELLER,
                    ResellerAgreementStatus.OFFER_EXTENDED_TO_BUYER,
                  ].includes(status as ResellerAgreementStatus)
                : true
            }
          />
        ) : (
          '-'
        ),
        authorizationName: (
          <ResponsiveColumn
            linkText={authorizationName || '-'}
            maxWidth={columnMaxWidth}
          />
        ),
        status: isEmpty(status) ? '-' : <StatusChipV4 {...statusDetails} />,
        actions: (
          <div
            style={{ padding: '16px' }}
            data-prevent-row-click
            data-testid={'actions'}
          >
            <ColumnActionWithMenuV2
              handleSelectOption={handleSelectOption(agreement)}
              menuOptions={getResaleMenuOptions(
                agreement.id as string,
                baseUrl,
                productId || '',
                cloudMarketplace === 'AWS'
                  ? agreement?.draft || false
                  : agreement?.offerStatus ===
                      ResellerAgreementStatus.OFFER_IN_DRAFT,
                cloudMarketplace,
                agreement,
                copiedResellerAuthorizationLink,
                routeCloud,
                accountId,
                externalBaseURL,
                secureKeyFromURL,
                partnerIdFromURL
              )}
            />
          </div>
        ) as never,
      }
    })
  const pricesArray = (
    cloudMarketplace === 'AWS'
      ? selectedResale?.isCustomPricing
        ? ['price']
        : getPricesArray(get(selectedResale, 'dimensions', []))
      : []
  ) as [keyof PriceTypes]

  const resaleList = (
    isAdmin ? getCustomObjectForAdmin() : getCustomObjectForLabraUI()
  ) as ResaleAuthorizationsListV2Props['resaleList']

  const handleCreateResaleAuthorization = () => {
    handleCloseResaleAuthorizationModal()
    if (externalBaseURL) {
      navigate({
        pathname:
          routeCloud && accountId
            ? `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${routeCloud}/accounts/${accountId}/product-listing/${selectedProductId}/resale-authorizations/edit`
            : `${externalBaseURL}/${selectedProductId || productId}/resale-authorizations/edit`,
        search: search,
      })
    } else if (routeCloud && accountId) {
      navigate({
        pathname: `/flyout/${routeCloud}/${accountId}/products-listing/${selectedProductId || productId}/resale-authorizations/edit`,
        search: search,
      })
    } else {
      navigate({
        pathname: `/flyout/products-listing/${selectedProductId || productId}/resale-authorizations/edit`,
        search: search,
      })
    }
  }

  const newUICreateBtn = (
    <div className={classes.toolTipView}>
      <LabraToolTipV2 tooltipPlacement="bottom">
        <ButtonV2
          variant="contained"
          styleType="primary"
          smallVariant
          className={classes.createBtn}
          onClick={handleOpenResaleAuthorizationModal}
          data-testid="create-resale-auth"
        >
          Create
        </ButtonV2>
      </LabraToolTipV2>
    </div>
  )

  const customCloudSelectorOptions: CustomCloudOptions = [
    { name: 'AWS', disabled: false },
    { name: 'AZURE', disabled: false },
    { name: 'GCP', disabled: true },
  ]
  const isOfferAndAuthorizations = pathname?.includes('offers-authorizations')

  const isFilterEnable = cloudMarketplace === 'AZURE'

  const onDeleteFiltersAppliedChip = useCallback(
    async (value: string) => {
      if (value.startsWith('Expiring in')) {
        setExpiryDate('')
        await dispatch(clearResaleAuthorizations())
        dispatch(
          getResellerAgreementData({
            partnerId: accountId,
            pageSize: pageSizeValue,
            pageNumber: 1,
            productId,
            cloud: cloudMarketplace,
            crmOpportunityId,
            ...(!isEmpty(inputValue) && !isEmpty(selectedOption)
              ? {
                  input: inputValue,
                  selectedInput:
                    getResaleAuthorizationSearchInput(selectedOption),
                  sectionName: 'resale-authorizations',
                }
              : {}),
            expiryDays: '',
            offerStatus: selectedOfferStatusCheckboxes,
          })
        )

        setAppliedFilteredValues([
          ...[],
          ...Object.keys(resaleAuthorizationStatus).filter(status =>
            selectedOfferStatusCheckboxes.includes(
              resaleAuthorizationStatus[status]
            )
          ),
        ])
      } else {
        setSelectedOfferStatusCheckboxes(prev => {
          const updatedStatusCheckboxes = prev.filter(
            status => status !== resaleAuthorizationStatus[value]
          )
          dispatch(clearResaleAuthorizations())
          dispatch(
            getResellerAgreementData({
              partnerId: accountId,
              pageSize: pageSizeValue,
              pageNumber: 1,
              productId,
              cloud: cloudMarketplace,
              crmOpportunityId,
              ...(!isEmpty(inputValue) && !isEmpty(selectedOption)
                ? {
                    input: inputValue,
                    selectedInput:
                      getResaleAuthorizationSearchInput(selectedOption),
                    sectionName: 'resale-authorizations',
                  }
                : {}),
              expiryDays: expiryDate,
              offerStatus: updatedStatusCheckboxes,
            })
          )
          setAppliedFilteredValues([
            ...(expiryDate ? [`Expiring in ${expiryDate} days`] : []),
            ...Object.keys(resaleAuthorizationStatus).filter(status =>
              updatedStatusCheckboxes.includes(
                resaleAuthorizationStatus[status]
              )
            ),
          ])
          return updatedStatusCheckboxes
        })
      }
    },
    [expiryDate, selectedOfferStatusCheckboxes]
  )

  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiryDate(e.target.value)
  }

  const handleDeleteCheckbox = (
    title: keyof typeof resaleAuthorizationStatus
  ) => {
    removeCheckbox(title)
  }

  const selectedCheckboxLabels = useMemo<string[]>(
    () =>
      Object.keys(resaleAuthorizationStatus).filter(key =>
        selectedOfferStatusCheckboxes.includes(resaleAuthorizationStatus[key])
      ),
    [selectedOfferStatusCheckboxes]
  )

  const expiryDateLabel = useMemo(
    () => getLabelFromMapping(expiryDateMapping, expiryDate),
    [expiryDate]
  )

  const applyFilters = useCallback(async () => {
    await dispatch(clearResaleAuthorizations())
    dispatch(
      getResellerAgreementData({
        partnerId: accountId,
        pageSize: pageSizeValue,
        pageNumber: 1,
        productId,
        cloud: cloudMarketplace,
        crmOpportunityId,
        ...(!isEmpty(inputValue) && !isEmpty(selectedOption)
          ? {
              input: inputValue,
              selectedInput: getResaleAuthorizationSearchInput(selectedOption),
              sectionName: 'resale-authorizations',
            }
          : {}),
        expiryDays: expiryDate,
        offerStatus: selectedOfferStatusCheckboxes,
      })
    )
    setAppliedFilteredValues([
      ...(expiryDate ? [`Expiring in ${expiryDate} days`] : []),
      ...Object.keys(resaleAuthorizationStatus).filter(status =>
        selectedOfferStatusCheckboxes.includes(
          resaleAuthorizationStatus[status]
        )
      ),
    ])
  }, [appliedFilteredValues, expiryDate, selectedOfferStatusCheckboxes])

  const handleCheckboxChange =
    (title: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target
      const valueToAddOrRemove = resaleAuthorizationStatus[title]

      setSelectedOfferStatusCheckboxes(prev => {
        const currentSet = new Set(prev)
        if (checked) {
          currentSet.add(valueToAddOrRemove)
        } else {
          currentSet.delete(valueToAddOrRemove)
        }

        return Array.from(currentSet)
      })
    }

  const removeCheckbox = (title: keyof typeof resaleAuthorizationStatus) => {
    const valueToRemove = resaleAuthorizationStatus[title]
    setSelectedOfferStatusCheckboxes(prev =>
      prev.filter(item => item !== valueToRemove)
    )
  }

  const sections: SectionProps[] = [
    {
      title: 'Expiry date',
      type: 'radio',
      content: (
        <div data-testid="expiry-date-radio" className={classes.filterSection}>
          <RadioButtonsV2
            checkedValue={{
              key: expiryDate || '',
              value: expiryDate || '',
            }}
            name="expiryDate"
            radioButtonMappings={expiryDateMapping}
            handleChange={handleExpiryDateChange}
            vertical={true}
            gapBetween={'0px'}
          />
        </div>
      ),
      selectedValues: expiryDateLabel || undefined,
      onDelete: () => setExpiryDate(''),
    },
    {
      title: 'Status',
      type: 'checkbox',
      content: (
        <div
          data-testid="private-offer-status-checkbox"
          className={classes.filterSection}
        >
          {Object.keys(resaleAuthorizationStatus).map(title => (
            <div className={classes.filterCheckbox}>
              <CheckBoxFieldv2
                key={title}
                keyName="privatefferStatus"
                title={title}
                value={selectedOfferStatusCheckboxes.includes(
                  resaleAuthorizationStatus[title]
                )}
                onChangeBox={handleCheckboxChange(title)}
              />
            </div>
          ))}
        </div>
      ),
      selectedValues: selectedCheckboxLabels,
      onDelete: handleDeleteCheckbox,
    },
  ]

  return (
    <>
      {products && !products?.length && !loading && !externalBaseURL ? (
        <ListingCreationPage />
      ) : (
        <>
          <div
            className={
              isAdmin
                ? classes.adminContainer
                : isOfferAndAuthorizations
                  ? classes.marginTopSpace
                  : ''
            }
          >
            <div
              className={clsx(
                externalBaseURL ? classes.externalApp : classes.upperContainter,
                classes.justifySpaceBetween,
                cloudMarketplace === 'AZURE' ? classes.justifyEnd : ''
              )}
            >
              {cloudMarketplace === 'AWS' && !externalBaseURL ? (
                <SearchBoxComponent
                  options={searchDropDownOptions.resaleAuthorizationOptions}
                  handleSearchClick={handleSearchClick}
                  loading={false}
                  searchNow={searchNow}
                  setSearchNow={setSearchNow}
                  showCrossMark={showCrossMark}
                  setShowCrossMark={setShowCrossMark}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  selectedOption={selectedOption}
                  setSelectedOption={setSelectedOption}
                />
              ) : (
                <></>
              )}
              <div
                className={clsx(
                  classes.dropdownAndProduct,
                  cloudMarketplace === 'AZURE' ? classes.gapBetween : ''
                )}
              >
                <div>
                  <CloudAccountChangeV2
                    cloudAccounts={filteredCloudAccounts}
                    selectedAccountId={selectedAccountIdWithCloud}
                    onChange={handleCloudAccountChange}
                    width={220}
                    size="small"
                  />
                </div>
                <div className={classes.dropDownWidth}>
                  <ProductSelectorDropdown
                    functionType={FlyoutFunction.RESALE_AUTHORIZATIONS}
                    externalBaseURL={externalBaseURL}
                    search={search}
                  />
                </div>
                <>{!isAdmin ? newUICreateBtn : null}</>
                {isFilterEnable && (
                  <>
                    <div className={classes.filterComponent}>
                      <Filters
                        sections={sections}
                        applyFilters={applyFilters}
                        minWidth="575px"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div
              className={clsx(
                classes.filtersAppliedContainer,
                appliedFilteredValues?.length ? classes.appliedFilter : ''
              )}
              data-testid="filters-applied-component"
            >
              <FiltersApplied
                selectedValues={appliedFilteredValues}
                onDelete={onDeleteFiltersAppliedChip}
                chipDisplayLimit={5}
                noBorder
              />
            </div>
            {searchNow && cloudMarketplace === 'AWS' ? (
              <>
                <SearchResultsCount totalCount={count} loading={loading} />
                {count > 0 && (
                  <ResaleAuthorizationsListV2
                    inputValue={inputValue}
                    selectedOption={selectedOption}
                    crmOpportunityId={crmOpportunityId}
                    externalBaseURL={externalBaseURL}
                    search={search}
                    resaleList={resaleList}
                    selectedResale={selectedResale as unknown as DetailedOffer}
                    openModal={openModal}
                    handleModalClose={handleClose}
                    pricesArray={pricesArray}
                    selectedCloudTypeProductListing={cloudMarketplace}
                    partnerId={accountId}
                    tableEl={tableEl}
                    expiryDate={expiryDate}
                    selectedOfferStatusCheckboxes={
                      selectedOfferStatusCheckboxes
                    }
                    appliedFilteredValues={appliedFilteredValues}
                  />
                )}
              </>
            ) : (
              <ResaleAuthorizationsListV2
                inputValue={inputValue}
                selectedOption={selectedOption}
                crmOpportunityId={crmOpportunityId}
                externalBaseURL={externalBaseURL}
                search={search}
                resaleList={resaleList}
                selectedResale={selectedResale as unknown as DetailedOffer}
                openModal={openModal}
                handleModalClose={handleClose}
                pricesArray={pricesArray}
                selectedCloudTypeProductListing={cloudMarketplace}
                partnerId={accountId}
                tableEl={tableEl}
                expiryDate={expiryDate}
                selectedOfferStatusCheckboxes={selectedOfferStatusCheckboxes}
                appliedFilteredValues={appliedFilteredValues}
              />
            )}
          </div>

          <ModalToCreateResaleAuthorization
            open={openNewResaleAuthorizationModal}
            onClose={handleCloseResaleAuthorizationModal}
            handleCreateResaleAuthorization={handleCreateResaleAuthorization}
            handleProductChange={handleProductChange}
            selectedAccountIdWithCloud={selectedAccountIdWithCloud}
            handleCloudAccountChange={handleCloudAccountChange}
            hubspotCloudSelector={<></>}
            externalBaseURL={externalBaseURL}
          />
          <DeleteAuthorizationModal
            open={openDeleteModal}
            onAccept={handleConfirmDeleteModal}
            acceptLoading={deleteLoading}
            onDecline={handleCloseDeleteModal}
          />
        </>
      )}
    </>
  )
}

export default ResaleAuthorizationsWrapperUIV2
